<template>
  <HeadTitle
    title="Clientes"
    description="En esta sección podrás ver los clientes de la plataforma y sus parametros de integración"
  />
  <Toolset
    ref="toolset"
    :searcher="refreshClients"
    add-button-icon="pi pi-plus"
    add-button-label="Crear Cliente"
    :to-add="toAdd"
    class="mt-3"
  />
  <br />
  <DataTable
    class="tabla mb-5"
    :value="clients"
    paginator
    :rows="limit"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    tableStyle="min-width: 50rem"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :totalRecords="totalRecords"
    :currentPageReportTemplate="currentPageReportTemplate"
    scrollable
    scrollHeight="600px"
    @page="onPageChange"
    :loading="loading"
  >
    <Column field="name" sortable header="Nombre"></Column>
    <Column field="code" sortable header="Código"></Column>
    <Column field="integrationType.name" header="Integración"></Column>
    <Column field="isInactive" sortable header="Estado">
      <template #body="{ data, field }">
        <Chip class="bg-pink-200 pt-1 pb-1" v-if="data[field]"> inactivo </Chip>
        <Chip class="bg-green-100 pt-1 pb-1" v-else> activo </Chip>
      </template>
    </Column>
    <Column field="updatedAt" header="Última actualización" sortable>
      <template #body="{ data, field }">
        <span>
          {{ formatDateWithHour(data[field]) }}
        </span>
      </template>
    </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <ConfirmPopup></ConfirmPopup>
        <div class="actions">
          <Button
            class="action-btn view p-button-sm"
            icon="pi pi-eye"
            @click="goToDetails(data['id'])"
            severity="primary"
            v-tooltip.bottom="'Ver detalles'"
          />
          <Button
            class="action-btn view p-button-sm bg-gray-300 border-gray-400 mr-2 shadow-1 text-primary"
            icon="pi pi-lock"
            @click="activateToggleClient($event, data['id'], data['enabled'])"
            severity="secondary"
            v-tooltip.bottom="'Cambiar estado'"
          />
          <Button
            class="action-btn view p-button-sm mr-2"
            severity="danger"
            icon="pi pi-trash"
            @click="deleteClient($event, data['id'])"
            v-tooltip.bottom="'Eliminar'"
          />
        </div>
      </template>
    </Column>
  </DataTable>
  <Footer>
    <template #left>
      <AuditButton
        :rootId="user?.roles.id"
        onlyDeleted
        label="Ver registros eliminados"
        :entities="['Client']"
      />
    </template>
  </Footer>
</template>
<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import Footer from '@/shared/components/Footer.vue'
  import Toolset from '@/shared/components/Toolset.vue'
  import {
    getClients,
    inactivateToggleClient,
    deleteClientById,
  } from '../services/clientService'
  import { useRouter } from 'vue-router'
  import { useConfirm } from 'primevue/useconfirm'
  import useNotifications from '@/store/notification'
  import { formatDateWithHour } from '@/shared/utils/fomartDateWithHours'
  import { useUserStore } from '@/store'
  import { storeToRefs } from 'pinia'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'

  const { user } = storeToRefs(useUserStore())

  const clients = ref()
  const search = ref('')
  const router = useRouter()
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const currentPage = ref()
  const lastPage = ref()
  const totalRecords = ref()
  const limit = ref(10)
  const loading = ref(false)

  const refreshClients = async () => {
    if (currentPage.value === lastPage.value) {
      isTheLastPage(currentPage.value, lastPage.value)
    } else {
      loading.value = true
      try {
        const response = await getClients(search.value)
        totalRecords.value = response.totalItems
        clients.value = response.data
      } finally {
        loading.value = false
      }
    }
  }

  onMounted(async () => {
    await refreshClients()
  })

  const searcher = async () => {
    loading.value = true
    try {
      const response = await getClients(search.value)
      clients.value = response.data
      totalRecords.value = response.totalItems
    } finally {
      loading.value = false
    }
  }

  const onPageChange = async (event: any) => {
    const { first, rows } = event
    limit.value = rows
    currentPage.value = Math.floor(first / rows + 1)
    lastPage.value = Math.floor(totalRecords.value / limit.value)
    await isTheLastPage(currentPage.value, lastPage.value)
  }

  const isTheLastPage = async (currPage: number, lastPage: number) => {
    if (currPage === lastPage) {
      loading.value = true
      try {
        const response = await getClients(
          search.value,
          currPage * limit.value * limit.value
        )
        clients.value = response.data
        totalRecords.value = response.totalItems
      } finally {
        loading.value = false
      }
    }
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${totalRecords.value}`
  })

  const goToDetails = (id: string) => {
    router.push('/clients/' + id)
  }

  const deleteClient = (event: any, id: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar este registro?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        const response = await deleteClientById(id)
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `Cliente eliminado con exito.`,
          })
          refreshClients()
        }
      },
      reject: () => {},
    })
  }

  const activateToggleClient = (
    event: any,
    id: string,
    isInactive: boolean
  ) => {
    confirm.require({
      target: event.currentTarget,
      message: `¿Realmente quieres ${
        isInactive ? 'habilitar' : 'inhabilitar'
      } este registro?`,
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-info',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        const response = await inactivateToggleClient(id)
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `El estado ha cambiado con exito.`,
          })

          refreshClients()
        }
      },
      reject: () => {},
    })
  }

  const toAdd = () => {
    router.push('/clients/add')
  }
</script>

<style scoped>
  .searcher {
    font-size: 14px;
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
  }

  .toolset {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 90%;
  }

  .actions {
    display: flex;
    max-width: 180px;
    justify-content: space-between;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    margin-right: 10px;
  }
</style>
